import React, { useEffect, useMemo } from 'react'
import type { QuoteFile } from 'paintscout'
import type { PresentationBlockProps } from '../..'
import { InlineEditableHtml, InlineEditableMedia } from '../../inlineEditable'
import compareBlockProps from '../../util/compareBlockProps'
import classnames from 'classnames'
import { getBlockPadding, getPosterStyles, isMediaEmpty } from '../../util'
import AnimationWrapper from '../../util/AnimationWrapper'
import { usePresentation, useQuote } from '../../../context'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined'
import { getBannerImage, preloadHomeImageSample } from './getBannerImage'
import { useDecodedBannerImage } from './useDecodedBannerImage'

const useStyles = makeStyles<
  Theme,
  PresentationBlockProps & {
    useGlassEffect?: boolean
    textBackgroundColor?: boolean
    noBackground?: boolean
    textColor?: string
    scrim?: string | null
  }
>(
  (theme: Theme) => {
    return {
      root: {},
      // `textColor` is resolved by `getPosterStyles`, not read off the block — a poster over an
      // image falls back to white even when the author never set a colour (#23083).
      content: (props) => ({
        ...(props.textColor && {
          textShadow:
            !props.block?.fields?.layout || props.block?.fields?.layout === 'poster' ? `0 0 9px black` : 'none',
          // A per-selection colour lands on a `<span>` inside the heading, so it keeps winning
          // over the value the heading passes down from this rule.
          '& h1, & h2, & h3, & p, & ol, & ul': {
            color: `${props.textColor} !important`
          }
        })
      }),
      posterWrapper: {
        position: 'relative',
        overflow: 'hidden'
      },
      posterImageWrapper: {
        position: 'absolute',
        height: '100%',
        width: '100%',
        overflow: 'hidden'
      },
      // Flat fill across the whole image rather than a bottom-edge gradient: a scraped hero can
      // carry its own headline and logo anywhere in the frame, so the text has to stay readable at
      // every point behind it, not just near one edge (#23083). Paints between the image wrapper
      // and `posterContentWrapper` on DOM order alone — the content wrapper's own `zIndex: 1`
      // keeps the text above it — and never eats a click meant for the image.
      posterScrim: (props) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        backgroundColor: props.scrim ?? 'transparent',
        pointerEvents: 'none'
      }),
      // Builder-only tag on the sample photo that stands in for the estimate's home image (#25097)
      homeImageTag: {
        position: 'absolute',
        top: theme.spacing(1.5),
        left: theme.spacing(1.5),
        zIndex: 2,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        borderRadius: 999,
        backgroundColor: 'rgba(255,255,255,0.94)',
        color: '#1a1a1a',
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.4,
        pointerEvents: 'none',
        '& svg': {
          fontSize: 14
        }
      },
      posterContentWrapper: (props) => ({
        padding: getBlockPadding({ block: props.block, source: 'block', size: 'desktop', contentWidth: 'md' }),
        position: 'relative',
        zIndex: 1,
        // This margin was hardcoded regardless of `background.noPadding`, unlike the `padding`
        // above it — a poster-layout banner marked "No Padding" still carried this vertical
        // margin, leaving a gap the checkbox couldn't remove (#22752/ENG-1439).
        margin: props.block?.background?.noPadding ? 0 : theme.spacing(10, 0),
        [theme.breakpoints.down('md')]: {
          padding: getBlockPadding({ block: props.block, source: 'block', size: 'tablet', contentWidth: 'md' })
        },
        [theme.breakpoints.down('sm')]: {
          margin: props.block?.background?.noPadding ? 0 : theme.spacing(7, 0),
          padding: getBlockPadding({ block: props.block, source: 'block', size: 'mobile', contentWidth: 'md' })
        }
      }),
      fullSize: {
        height: '100%',
        width: '100%'
      },
      collageWrapper: (
        props,
        imagePosition = props.block?.fields?.layoutOptions?.collage?.imagePosition,
        centeredImage = imagePosition === 'top' || imagePosition === 'bottom'
      ) => ({
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gridTemplateRows: `repeat(8, 1fr) auto`,
        ...(centeredImage && {
          // 56px is equal to content width md padding
          gridTemplateColumns: '1fr',
          gridTemplateRows: `1fr ${theme.spacing(18)}px auto`
        }),
        [theme.breakpoints.down('xs')]: {
          display: 'block'
        }
      }),
      collageImageWrapper: (
        props,
        imagePosition = props.block?.fields?.layoutOptions?.collage?.imagePosition,
        verticalAlign = props.block?.fields?.layoutOptions?.collage?.verticalAlign ?? 'start',
        centeredImage = imagePosition === 'top' || imagePosition === 'bottom'
      ) => ({
        alignSelf: verticalAlign,
        gridColumn: getGridPosition({ imagePosition, verticalAlign, size: 'desktop' }).image.gridColumn,
        gridRow: getGridPosition({ imagePosition, verticalAlign, size: 'desktop' }).image.gridRow,
        [theme.breakpoints.down('md')]: {
          gridColumn: getGridPosition({ imagePosition, verticalAlign, size: 'tablet' }).image.gridColumn
        },
        ...(centeredImage && {
          margin: getBlockPadding({
            block: props.block,
            source: 'block',
            size: 'desktop',
            contentWidth: 'md',
            horizontalOnly: true
          }),
          [theme.breakpoints.down('md')]: {
            margin: getBlockPadding({
              block: props.block,
              source: 'block',
              size: 'tablet',
              contentWidth: 'md',
              horizontalOnly: true
            })
          },
          [theme.breakpoints.down('sm')]: {
            margin: getBlockPadding({
              block: props.block,
              source: 'block',
              size: 'mobile',
              contentWidth: 'md',
              horizontalOnly: true
            })
          },
          [theme.breakpoints.down('xs')]: {
            margin: '0 !important'
          },
          zIndex: 1
        })
      }),
      collageImage: {
        [theme.breakpoints.down('xs')]: {
          clipPath: 'none !important'
        }
      },
      collageContentWrapper: (
        props,
        useGlassEffect = props.useGlassEffect,
        textBackgroundColor = props.textBackgroundColor,
        noBackground = props.noBackground,
        imagePosition = props.block?.fields?.layoutOptions?.collage?.imagePosition,
        verticalAlign = props.block?.fields?.layoutOptions?.collage?.verticalAlign ?? 'start',
        centeredImage = imagePosition === 'top' || imagePosition === 'bottom',
        inset = !!props.block?.fields?.contentWidth && props.block?.fields?.contentWidth !== 'full'
      ) => ({
        // padding in this block is equal to content width padding
        alignSelf: verticalAlign,
        transition: 'all .3s ease',
        ...(!noBackground && {
          backgroundColor: useGlassEffect ? 'rgba(255, 255, 255, .25)' : textBackgroundColor,
          backdropFilter: useGlassEffect ? 'blur(5px) saturate(80%) contrast(80%) brightness(130%)' : '',
          padding: theme.spacing(imagePosition === 'top' ? 24 : 7, 7, imagePosition === 'bottom' ? 24 : 7, 7),
          ...(!centeredImage && { zIndex: 1 })
        }),
        ...(noBackground && {
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          padding: theme.spacing(0, 7),
          zIndex: 2
        }),
        gridColumn: getGridPosition({ imagePosition, verticalAlign, size: 'desktop' }).content.gridColumn,
        gridRow: getGridPosition({ imagePosition, verticalAlign, size: 'desktop' }).content.gridRow,
        [theme.breakpoints.down('md')]: {
          gridColumn: getGridPosition({ imagePosition, verticalAlign, size: 'tablet' }).content.gridColumn,
          ...(!noBackground && {
            padding: theme.spacing(imagePosition === 'top' ? 22 : 4, 4, imagePosition === 'bottom' ? 22 : 4, 4)
          }),
          ...(noBackground && {
            padding: theme.spacing(0, 4)
          })
        },
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(imagePosition === 'top' ? 22 : 4, 2, imagePosition === 'bottom' ? 22 : 4, 2)
        },
        [theme.breakpoints.down('xs')]: {
          ...(!noBackground && {
            padding: theme.spacing(2)
          }),
          ...(noBackground && {
            padding: theme.spacing(2, inset ? 0 : 2)
          })
        }
      })
    }
  },
  { name: 'BannerBlock' }
)

function BannerBlock(props: PresentationBlockProps) {
  const { isEditable, isSettings, presetColors, block, blockPrefix, setFieldValue, submitForm, WrapperComponent } =
    props
  const useGlassEffect =
    block?.fields?.layoutOptions?.collage?.useGlassEffect && CSS.supports('backdrop-filter', 'blur(4px)')
  const textBackgroundColor = block?.fields?.layoutOptions?.collage?.textBackgroundColor
  const noBackground = block?.fields.layoutOptions?.collage?.noBackground ?? false
  const { presentationLibrary } = usePresentation()
  const { quote } = useQuote()

  const { animation, content, layout = 'poster' } = block.fields
  // Memoized so the image keeps its identity between renders: useDecodedBannerImage decodes again
  // on every new object, and InlineEditableMedia re-syncs its copy of the file (#25097).
  const nextBannerImage = useMemo(
    () => getBannerImage({ block, quote, isSettings }),
    // getBannerImage reads only these two fields off the block; depending on `block` itself would
    // recompute on every edit to any other field of it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [block.fields?.image, block.fields?.useHomeImage, quote, isSettings]
  )
  const { image, isHomeImage } = useDecodedBannerImage(nextBannerImage)

  // Cached before the author first ticks "Use home image from estimate", so the first swap doesn't
  // wait on a download (#25097)
  useEffect(() => {
    if (isSettings) {
      preloadHomeImageSample()
    }
  }, [isSettings])
  const isAnimated = ['slide', 'fade', 'zoom'].includes(animation)

  const imagePosition = props.block?.fields?.layoutOptions?.collage?.imagePosition
  const allowVideo = layout === 'collage' && ['top', 'bottom'].includes(imagePosition)

  const { textColor, scrim } = getPosterStyles({
    layout,
    hasImage: !isMediaEmpty({ file: image }),
    color: block.fields?.layoutOptions?.[layout]?.color,
    overlay: block.fields?.layoutOptions?.poster?.overlay
  })

  const classes = useStyles({ useGlassEffect, textBackgroundColor, noBackground, textColor, scrim, ...props })

  const handleEditField = (field: string, content: string | QuoteFile) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  const imageWrapperClass = classnames({
    [classes.posterImageWrapper]: layout === 'poster',
    [classes.collageImageWrapper]: layout === 'collage'
  })

  const contentWrapperClass = classnames({
    [classes.posterContentWrapper]: layout === 'poster',
    [classes.collageContentWrapper]: layout === 'collage',
    ['themeBackground']: layout === 'collage' && !useGlassEffect && !textBackgroundColor && !noBackground
  })

  const mediaRootClass = classnames({
    [classes.fullSize]: layout === 'poster'
  })

  const mediaImageClass = classnames({
    [classes.fullSize]: layout === 'poster',
    [classes.collageImage]: layout === 'collage'
  })

  const mediaSizeClass = classnames({
    [classes.fullSize]: layout === 'poster'
  })

  return (
    <WrapperComponent {...props}>
      <div
        className={classnames({
          [classes.posterWrapper]: layout === 'poster',
          [classes.collageWrapper]: layout === 'collage'
        })}
      >
        <AnimationWrapper className={imageWrapperClass} animation={animation} index={0} direction={'right'}>
          <InlineEditableMedia
            // Remount when the image source switches. makeStyles applies the anchor and aspect ratio
            // in an effect after paint, so an in-place swap showed a frame with the wrong ones; a new
            // instance builds its styles during render. useDecodedBannerImage has already decoded
            // the image, so the new <img> paints in its first frame (#25097).
            key={isHomeImage ? 'home-image' : 'block-image'}
            mediaOptionsProps={{
              blockPrefix: blockPrefix,
              fieldSuffix: 'image'
            }}
            accept={allowVideo ? 'video/*,image/*' : 'image/*'}
            classes={{ root: mediaRootClass, image: mediaImageClass, size: mediaSizeClass }}
            file={image}
            getFocusFromSection={layout === 'poster'}
            // the estimate's photo has no crop options to save, so it opens no media options either
            disableMediaDrillIn={layout === 'poster' || isHomeImage}
            // The estimate's photo is resolved per render and must never be saved over the
            // block's own image, which is the fallback for an estimate with no photo.
            handleEdit={(file) => {
              if (!isHomeImage) {
                handleEditField('fields.image', file)
              }
            }}
            hasRightSideMenus={layout === 'collage' && imagePosition === 'right' ? true : false}
            isAnimated={isAnimated}
            isEditable={isEditable}
            isSettings={isSettings}
            placeholderButtonText={'Add Image'}
            presentationLibrary={presentationLibrary}
            youtube={allowVideo}
          />
        </AnimationWrapper>
        {scrim && <div className={classes.posterScrim} />}
        {isHomeImage && isSettings && (
          <div className={classes.homeImageTag}>
            <HomeOutlinedIcon />
            Custom image from estimate
          </div>
        )}
        <AnimationWrapper
          className={contentWrapperClass}
          active={layout !== 'poster'}
          animation={animation}
          index={1}
          direction={'bottom'}
        >
          <AnimationWrapper active={layout === 'poster'} animation={animation} index={1} direction={'bottom'}>
            <InlineEditableHtml
              classes={{ root: classnames({ [classes.content]: !!textColor }) }}
              content={content}
              // Without a `fieldKey` the inspector never sees a focused text element, so banner
              // copy had no text toolbar at all — no per-selection colour, bold, align or link
              // (#23083). Every other rich-text field in the tree passes one.
              fieldKey={`${blockPrefix}.fields.content`}
              handleEdit={(content: string) => handleEditField('fields.content', content)}
              isEditable={isEditable}
              isSettings={isSettings}
              presetColors={presetColors}
            />
          </AnimationWrapper>
        </AnimationWrapper>
      </div>
    </WrapperComponent>
  )
}

function getGridRow(verticalAlign: string) {
  if (verticalAlign === 'start') return '2 / -1'
  if (verticalAlign === 'center') return '1 / -1'
  if (verticalAlign === 'end') return '1 / 8'
}

function getGridPosition(args: {
  imagePosition: string
  verticalAlign: string
  size: 'desktop' | 'tablet' | 'mobile'
}) {
  const { imagePosition, verticalAlign, size } = args
  // default left
  let image = { gridColumn: '1 / 7', gridRow: '1 / -1' }
  let content = { gridColumn: '5 / -1', gridRow: getGridRow(verticalAlign) }

  if (imagePosition === 'bottom') {
    image = { gridColumn: '1 / -1', gridRow: '2 / -1' }
    content = { gridColumn: '1 / -1', gridRow: '1 / 3' }
  }

  if (imagePosition === 'top') {
    image = { gridColumn: '1 / -1', gridRow: '1 / 3' }
    content = { gridColumn: '1 / -1', gridRow: '2 / -1' }
  }

  if (imagePosition === 'right') {
    image = { gridColumn: '3 / -1', gridRow: '1 / -1' }
    content = { gridColumn: '1 / 5', gridRow: getGridRow(verticalAlign) }
    if (size === 'tablet') {
      content.gridColumn = '1 / 5'
      image.gridColumn = '2 / -1'
    }
    if (size === 'mobile') {
      image = { gridColumn: '1 / -1', gridRow: '1 / 3' }
      content = { gridColumn: '1 / -1', gridRow: '2 / -1' }
    }
  }

  if (imagePosition === 'left') {
    image = { gridColumn: '1 / 7', gridRow: '1 / -1' }
    content = { gridColumn: '5 / -1', gridRow: getGridRow(verticalAlign) }
    if (size === 'tablet') {
      content.gridColumn = '5 / -1'
      image.gridColumn = '1 / 8'
    }
    if (size === 'mobile') {
      image = { gridColumn: '1 / -1', gridRow: '1 / 3' }
      content = { gridColumn: '1 / -1', gridRow: '2 / -1' }
    }
  }

  return { image, content }
}

export default React.memo(BannerBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
